import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLaunchParams } from '@telegram-apps/sdk-react';
import { PositionTile } from '@/components/PositionTile';
import { TabSelector } from '@/components/TabSelector';
import { PositionDetailDrawer } from '@/components/PositionDetailDrawer';
import { PositionTradeDrawer } from '@/components/PositionTradeDrawer';
import { TickerSymbolDrawer } from '@/components/TickerSymbolDrawer';
import { ExchangeFilterDrawer, ExchangeFilterOption } from '@/components/ExchangeFilterDrawer';
import { StatsDrawer } from '@/components/StatsDrawer';
import { useOpenPositions, useInfiniteClosedPositions } from '@/hooks/usePositions';
import { PositionData, ClosedPositionData } from '@crypto-trading/common/browser';
import { Loader2, Plus, Filter, BarChart3 } from 'lucide-react';
import { ReferralBottomNav } from '@/components/ReferralBottomNav';
import { AssetClassToggle, type AssetClass } from '@/components/AssetClassToggle';
import { StockTickerDrawer } from '@/components/StockTickerDrawer';
import { useStockInstruments } from '@/hooks/useStockNews';
import { buildStockTradeTarget, isStockPosition, type StockTradeTarget } from '@/stock/stock-trade';
import { trackEvent } from '@/utils/analytics';

const POSITIONS_PER_PAGE = 10;

export const AllPositionsPageMobile: React.FC = () => {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || '';
  
  const [activeTab, setActiveTab] = useState<'opened' | 'closed'>('opened');
  const [selectedPosition, setSelectedPosition] = useState<PositionData | ClosedPositionData | null>(null);
  const [isTickerDrawerOpen, setIsTickerDrawerOpen] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [exchangeFilter, setExchangeFilter] = useState<ExchangeFilterOption>('all');
  const [assetClass, setAssetClass] = useState<AssetClass>('crypto');
  const [stockTickerOpen, setStockTickerOpen] = useState(false);
  const [stockTradeTarget, setStockTradeTarget] = useState<StockTradeTarget | null>(null);
  const [isExchangeFilterDrawerOpen, setIsExchangeFilterDrawerOpen] = useState(false);
  const [isStatsDrawerOpen, setIsStatsDrawerOpen] = useState(false);
  
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef<number | null>(null);
  const isPulling = useRef(false);

  // React Query hooks
  const exchangeCodeFilter = exchangeFilter === 'all' ? undefined : exchangeFilter;
  const { data: openPositions = [], isLoading: isLoadingOpen, refetch: refetchOpen } = useOpenPositions(
    userId,
    activeTab === 'opened',
    exchangeCodeFilter
  );

  const {
    data: closedPositionsData,
    isLoading: isLoadingClosed,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchClosed,
  } = useInfiniteClosedPositions(userId, POSITIONS_PER_PAGE, activeTab === 'closed', exchangeCodeFilter);

  const closedPositions = closedPositionsData?.pages.flatMap((page) => page.positions) ?? [];
  const positions = activeTab === 'opened' ? openPositions : closedPositions;
  const { data: stockInstruments = [], isSuccess: stockCatalogReady } = useStockInstruments();
  const stockTickers = useMemo(
    () => new Set(stockInstruments.map((row) => row.ticker)),
    [stockInstruments]
  );
  const stockTickerList = useMemo(() => [...stockTickers].sort(), [stockTickers]);
  const visiblePositions = positions.filter((position) => {
    if (!stockCatalogReady) return assetClass === 'crypto';
    const stock = isStockPosition(position.symbol, stockTickers);
    return assetClass === 'stocks' ? stock : !stock;
  });
  const isLoading = activeTab === 'opened' ? isLoadingOpen : isLoadingClosed;
  const hasMore = activeTab === 'closed' ? hasNextPage ?? false : false;

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      if (activeTab === 'opened') {
        await refetchOpen();
      } else {
        await refetchClosed();
      }
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [isRefreshing, activeTab, refetchOpen, refetchClosed]);

  // Pull-to-refresh handlers
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const getPageScrollTop = () => {
      const currentWindow = typeof globalThis === 'undefined' ? undefined : globalThis.window;
      if (!currentWindow) return 0;
      return currentWindow.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    };

    const isAtTop = () => {
      const containerTop = container.scrollTop;
      const pageTop = getPageScrollTop();
      return containerTop <= 0 && pageTop <= 0;
    };

    const resetPullState = () => {
      isPulling.current = false;
      touchStartY.current = null;
      setPullDistance(0);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (isAtTop()) {
        touchStartY.current = e.touches[0].clientY;
        isPulling.current = true;
      } else {
        resetPullState();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || touchStartY.current === null) return;
      if (!isAtTop()) {
        resetPullState();
        return;
      }
      const currentY = e.touches[0].clientY;
      const distance = currentY - touchStartY.current;
      if (distance > 0) {
        e.preventDefault();
        const maxPull = 40;
        setPullDistance(Math.min(distance, maxPull));
      } else {
        resetPullState();
      }
    };

    const handleTouchEnd = () => {
      if (isPulling.current && pullDistance > 35 && !isRefreshing) {
        handleRefresh();
      } else {
        setPullDistance(0);
      }
      touchStartY.current = null;
      isPulling.current = false;
    };

    const handleTouchCancel = () => {
      resetPullState();
    };

    const handleScroll = () => {
      if (!isAtTop()) {
        resetPullState();
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchCancel);
    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [pullDistance, isRefreshing, handleRefresh]);

  // Infinite scroll observer for closed positions
  useEffect(() => {
    if (activeTab !== 'closed' || !hasMore || isLoading || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, isFetchingNextPage, activeTab, fetchNextPage]);

  const handleOpenTickerDrawer = () => {
    trackEvent('custom_trade_attempt', { page: 'positions', userId });
    if (assetClass === 'stocks') {
      setStockTickerOpen(true);
      return;
    }
    setIsTickerDrawerOpen(true);
  };

  const handleTickerSubmit = useCallback((normalizedSymbol: string) => {
    setTradeSymbol(normalizedSymbol);
    setIsTickerDrawerOpen(false);
    setIsTradeDrawerOpen(true);
  }, []);

  const handleTradeDrawerClose = (open: boolean) => {
    setIsTradeDrawerOpen(open);
    if (!open) {
      setTradeSymbol(null);
      setStockTradeTarget(null);
    }
  };

  const handleStockTickerSelect = useCallback((ticker: string) => {
    const target = buildStockTradeTarget(ticker, stockInstruments);
    setStockTradeTarget(target);
    setTradeSymbol(target.symbol);
    setStockTickerOpen(false);
    setIsTradeDrawerOpen(true);
  }, [stockInstruments]);

  const getExchangeDisplayName = (exchange: ExchangeFilterOption): string => {
    const exchangeMap: Record<ExchangeFilterOption, string> = {
      all: 'All',
      bybit: 'Bybit',
      binance: 'Binance',
      gate: 'Gate.io',
      aster: 'Aster',
      kucoin: 'KuCoin',
      hyperliquid: 'Hyperliquid',
    };
    return exchangeMap[exchange] || exchange;
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col">     
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
        <div className="max-w-2xl mx-auto">
          {/* Tab Toggle - Sticky */}
          <div className="sticky top-0 z-20 bg-primary pt-4 pb-2 px-4">
            <div className="mb-3">
              <AssetClassToggle value={assetClass} onChange={setAssetClass} />
            </div>
            <TabSelector
              options={[
                { value: 'opened', label: 'Opened' },
                { value: 'closed', label: 'Closed' },
              ]}
              value={activeTab}
              onChange={setActiveTab}
            />
            
            {/* Exchange Filter - Below tabs */}
            <div className="flex items-center gap-2 mt-3 mb-2">
              <span className="text-sm text-text-secondary">Display positions for</span>
              <button
                onClick={() => setIsExchangeFilterDrawerOpen(true)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary/40 border border-gray-600 text-text-primary hover:bg-primary/60 transition-colors text-sm font-medium"
              >
                <span>{getExchangeDisplayName(exchangeFilter)}</span>
                <Filter className="w-3.5 h-3.5" />
              </button>
            </div>
            
            {/* Pull-to-refresh indicator */}
            <div 
              className="flex justify-center items-center overflow-hidden transition-all duration-300 ease-out"
              style={{
                transform: `translateY(${Math.max(0, pullDistance - 20)}px)`,
                opacity: (pullDistance > 0 || isRefreshing) ? Math.min(1, pullDistance / 35) : 0,
                maxHeight: (pullDistance > 0 || isRefreshing) ? '3rem' : '0',
                paddingTop: (pullDistance > 0 || isRefreshing) ? '0.5rem' : '0',
                paddingBottom: (pullDistance > 0 || isRefreshing) ? '0.5rem' : '0',
                marginBottom: (pullDistance > 0 || isRefreshing) ? '0.5rem' : '0',
                pointerEvents: (pullDistance > 0 || isRefreshing) ? 'auto' : 'none',
                transition: pullDistance === 0 
                  ? 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1), max-height 0.3s cubic-bezier(0.16, 1, 0.3, 1), padding 0.3s cubic-bezier(0.16, 1, 0.3, 1), margin 0.3s cubic-bezier(0.16, 1, 0.3, 1)' 
                  : 'opacity 0.2s ease-out',
              }}
            >
              {isRefreshing ? (
                <Loader2 className="w-5 h-5 animate-spin text-accent" />
              ) : (
                <div className="text-xs text-text-secondary">
                  {pullDistance > 35 ? 'Release to refresh' : 'Pull to refresh'}
                </div>
              )}
            </div>
          </div>
          
          <div 
            className="px-4 pb-4 transition-transform duration-300 ease-out"
            style={{
              transform: pullDistance > 0 ? `translateY(${Math.min(pullDistance * 0.3, 20)}px)` : 'translateY(0)',
            }}
          >
            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            )}

            {/* Positions List */}
            {!isLoading && visiblePositions.length === 0 && (
              <div className="text-center py-12">
                <p className="text-text-secondary">
                  No {activeTab} positions found
                </p>
              </div>
            )}

            {!isLoading && (
              <div className="space-y-3">
                {visiblePositions.map((position) => (
                  <PositionTile
                    key={'id' in position ? position.id : position.positionId}
                    position={position}
                    onClick={() => setSelectedPosition(position)}
                  />
                ))}
              </div>
            )}

            {/* Load More Trigger */}
            {!isLoading && hasMore && (
              <div ref={loadMoreRef} className="flex justify-center py-8">
                {isFetchingNextPage && (
                  <Loader2 className="w-6 h-6 animate-spin text-accent" />
                )}
              </div>
            )}

            {/* End of List */}
            {!isLoading && !hasMore && visiblePositions.length > 3 && (
              <div className="text-center py-8">
                <p className="text-text-secondary text-sm">
                  End of {activeTab} positions
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <button
        onClick={() => setIsStatsDrawerOpen(true)}
        className="fixed bottom-40 right-4 z-40 w-14 h-14 rounded-full bg-primary border-2 border-accent hover:bg-primary/80 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="View statistics"
      >
        <BarChart3 className="w-6 h-6 text-accent" />
      </button>

      <button
        onClick={handleOpenTickerDrawer}
        className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-accent hover:bg-accent/80 flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
        aria-label="Open new trade"
      >
        <Plus className="w-6 h-6 text-white" />
      </button>

      <ReferralBottomNav />

      <TickerSymbolDrawer
        open={isTickerDrawerOpen}
        onOpenChange={setIsTickerDrawerOpen}
        onSubmit={handleTickerSubmit}
        title="Enter Ticker Symbol"
        submitButtonText="Continue to Trade"
      />

      <ExchangeFilterDrawer
        open={isExchangeFilterDrawerOpen}
        onOpenChange={setIsExchangeFilterDrawerOpen}
        selectedExchange={exchangeFilter}
        onSelect={setExchangeFilter}
      />

      <StatsDrawer
        userId={userId}
        open={isStatsDrawerOpen}
        onOpenChange={setIsStatsDrawerOpen}
      />

      <StockTickerDrawer
        open={stockTickerOpen}
        onOpenChange={setStockTickerOpen}
        tickers={stockTickerList}
        onSelect={handleStockTickerSelect}
      />

      <PositionTradeDrawer
        userId={userId}
        symbol={tradeSymbol}
        displayTicker={stockTradeTarget?.displayTicker}
        allowedExchangeCodes={stockTradeTarget?.allowedExchangeCodes}
        symbolByExchangeCode={stockTradeTarget?.symbolByExchangeCode}
        open={isTradeDrawerOpen}
        onOpenChange={handleTradeDrawerClose}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
            setIsTradeDrawerOpen(false);
          }
        }}
      />

      <PositionDetailDrawer
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        onPositionUpdate={(updatedPosition) => setSelectedPosition(updatedPosition)}
        source="positions_page"
      />
    </div>
  );
};
